<template>
  <b-modal
      id="title-generator-modal"
      centered
      :title="$t('pages.keyword.generator.title')"
      title-class="font-18"
      size="lg"
      @ok="submit"
      @show="handleModalShow"
      @hidden="handleModalHide"
  >
    <div v-if="!loading && !title">
      <b-form-group
          class="mb-3"
          :label="$t('pages.keyword.generator.product_label')"
          label-for="product-description"
      >
        <b-form-input
            id="product-description"
            type="text"
            v-model="product"
            :placeholder="$t('pages.keyword.generator.product_placeholder')"
            :class="{'is-invalid': submitted && $v.product.$error }"
        ></b-form-input>
        <div
            v-if="submitted && !$v.product.required"
            class="invalid-feedback"
        >
          {{ $t('pages.keyword.generator.validations.product_required') }}
        </div>
      </b-form-group>

      <b-form-group
          class="mb-3"
          :label="$t('pages.keyword.generator.brand_label')"
          label-for="brand"
      >
        <b-form-input
            id="brand"
            v-model="brand"
            type="text"
            :placeholder="$t('pages.keyword.generator.brand_placeholder')"
            :class="{'is-invalid': submitted && $v.brand.$error }"
        ></b-form-input>
        <div
            v-if="submitted && !$v.brand.required"
            class="invalid-feedback"
        >
          {{ $t('pages.keyword.generator.validations.brand_required') }}
        </div>
      </b-form-group>

      <b-form-group
          class="mb-3"
      >
        <template #label>
          <div>{{ $t('pages.keyword.generator.creativity_label') }} / <strong>{{ creativityLabel }}</strong></div>
        </template>
        <b-form-input
            class="form-range custom-range"
            v-model="creativity"
            id="creativity"
            type="range"
            min="0.6"
            max="0.9"
            step="0.01"
            :placeholder="$t('pages.keyword.generator.creativity_placeholder')"
        ></b-form-input>
      </b-form-group>

      <b-form-group
          class="mb-3"
          :label="$t('pages.keyword.generator.keywords_label')"
          label-for="keywords"
      >
        <multiselect
            v-model="selectedKeywords"
            :tag-placeholder="$t('pages.keyword.generator.tag_placeholder')"
            :placeholder="$t('pages.keyword.generator.keywords_placeholder')"
            :options="options"
            :multiple="true"
            :allow-empty="false"
            :max="max"
            @tag="addCustomKeyword"
            :taggable="true">
        </multiselect>
      </b-form-group>
    </div>

    <div v-if="!loading && title">
      <div class="bg-light p-3" style="min-height: 120px; border-radius: 6px">
        <vue-typer
            :text="title"
            :repeat="0"
            :type-delay='30'
            @completed="showTitleCopy = true"
            caret-animation='smooth'
        />
        <b-btn class="mx-2" variant="outline-secondary" size="sm" v-if="showTitleCopy" v-clipboard:copy="title">
          <i class="mdi mdi-content-copy"></i>
          {{ $t('pages.keyword.generator.copy') }}
        </b-btn>
      </div>
    </div>

    <div v-if="loading">
      <b-skeleton width="85%"></b-skeleton>
      <b-skeleton width="55%"></b-skeleton>
      <b-skeleton width="60%"></b-skeleton>
      <b-skeleton width="45%"></b-skeleton>
      <b-skeleton width="75%"></b-skeleton>
      <b-skeleton width="85%"></b-skeleton>
      <b-skeleton width="95%"></b-skeleton>
    </div>

    <template #modal-footer="{ cancel }">
      <b-button variant="danger" v-if="title" @click="cancel()" :disabled="!showTitleCopy">
        {{ $t('pages.keyword.generator.close') }}
      </b-button>

      <b-btn v-else :disabled="loading" @click="submit" variant="success">
        <b-spinner small v-if="loading"></b-spinner>
        {{ $t('pages.keyword.generator.generate') }}
      </b-btn>
    </template>

  </b-modal>
</template>

<script>
import KeywordService from "../../../../Services/Api/KeywordService";
import {keywordComputed, keywordMethods} from "@/state/helpers";
import Multiselect from 'vue-multiselect'
import {VueTyper} from 'vue-typer'
import {required} from "vuelidate/lib/validators";

export default {
  name: "TitleGeneratorModal",
  components: {
    Multiselect,
    VueTyper
  },
  props: ['options'],
  data() {
    return {
      product: '',
      brand: '',
      title: null,
      selectedKeywords: [],
      max: 10,
      creativity: 0.85,
      loading: false,
      showTitleCopy: false,
      submitted: false
    }
  },
  validations: {
    product: {
      required,
    },
    brand: {
      required
    }
  },
  computed: {
    ...keywordComputed,
    creativityLabel() {
      const creativity = parseFloat(this.creativity)
      if (creativity <= 0.75) {
        return this.$t('pages.keyword.generator.creativity_labels.formal')
      }
      if (creativity <= 0.80) {
        return this.$t('pages.keyword.generator.creativity_labels.simple')
      }
      if (creativity <= 0.90) {
        return this.$t('pages.keyword.generator.creativity_labels.creative')
      }
      return ''
    }
  },
  methods: {
    ...keywordMethods,
    async submit() {
      if (this.title) {
        this.$bvModal.hide('title-generator-modal')
        return
      }

      this.submitted = true
      this.$v.$touch();

      if (this.$v.$invalid) {
        return false;
      }

      this.loading = true
      const payload = {
        product: this.product,
        brand: this.brand,
        keywords: this.selectedKeywords,
        creativity: parseFloat(this.creativity)
      }
      const {data: title} = await KeywordService.generateTitle({payload}).finally(() => {
        this.loading = false
      })
      this.title = title.text
    },
    addCustomKeyword(e) {
      if (this.selectedKeywords.length < this.max) {
        this.selectedKeywords.push(e)
      }
    },
    handleModalShow() {
      this.selectedKeywords = [...this.keywordList.slice(0, this.max)]
    },
    handleModalHide() {
      this.submitted = false
      this.selectedKeywords = []
      this.brand = ''
      this.product = ''
      this.title = null
      this.creativity = 0.85
      this.showTitleCopy = false
    }
  }
}
</script>

<style scoped lang="scss">
::v-deep .vue-typer {
  .custom.char {
    font-size: 20px !important;
    color: #495057 !important;
    font-weight: 500 !important;
  }
}


</style>